import { format } from 'date-fns';
import type { AppSettings, BankHoliday, BookingType } from '@/types';
import { isBankHoliday } from '@/utils/heatmap-utils';

export type NonWorkingDayReason = 'bank-holiday' | 'working-profile';

export interface BlockedDragDay {
  day: Date;
  reason: NonWorkingDayReason;
}

/**
 * The first day a drag-created booking would cover that the resource does not work:
 * a bank holiday, or a day their working profile gives no hours. Null when the whole
 * range is workable.
 *
 * `lastCoveredDay` is inclusive — Bryntum reports an exclusive end date, so callers
 * step back from it before calling.
 */
export function findBlockedDragDay(
  firstDay: Date,
  lastCoveredDay: Date,
  bankHolidays: BankHoliday[],
  isNonWorkingDay: (day: Date) => boolean,
): BlockedDragDay | null {
  const day = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate());
  const last = new Date(lastCoveredDay.getFullYear(), lastCoveredDay.getMonth(), lastCoveredDay.getDate());

  while (day <= last) {
    const current = new Date(day);
    if (isBankHoliday(current, bankHolidays)) return { day: current, reason: 'bank-holiday' };
    if (isNonWorkingDay(current)) return { day: current, reason: 'working-profile' };
    day.setDate(day.getDate() + 1);
  }

  return null;
}

/**
 * The booking type a drag-created booking would be given: the company default, or the
 * first selectable type, matching how the booking form seeds its own selection.
 */
export function resolveNewBookingType(
  bookingTypes: BookingType[],
  settings: Pick<AppSettings, 'timelineDefaultBookingType' | 'defaultBookingTypeId'>,
): BookingType | undefined {
  const defaultId = settings.timelineDefaultBookingType || settings.defaultBookingTypeId;
  const configuredDefault = defaultId
    ? bookingTypes.find(bt => String(bt.id) === String(defaultId) && bt.active)
    : undefined;

  return configuredDefault ?? bookingTypes.find(bt => bt.active) ?? bookingTypes[0];
}

/**
 * Whether a booking of this type must stay inside the resource's working profile.
 * Auto-adjusting types are excluded: they shift their own dates rather than being
 * refused, so a drag over a non-working day is legitimate for them.
 */
export function obeysWorkingProfile(bookingType: BookingType | undefined): boolean {
  return Boolean(
    bookingType?.obeyUserWorkingProfileAndBankHolidays
    && !bookingType.autoAdjustBookingForWorkingProfileAndBankHolidays,
  );
}

/** Why the drag was refused, in the same terms as the API's own rejection message. */
export function blockedDragMessage(resourceName: string, blocked: BlockedDragDay): string {
  const name = resourceName.trim() || 'This resource';
  const day = format(blocked.day, 'EEE d MMM yyyy');
  const reason = blocked.reason === 'bank-holiday'
    ? 'is a bank holiday'
    : 'is outside their working profile';

  return `${name} cannot be booked over ${day}, which ${reason}.`;
}
